/**
 * Interactive Tour System
 * 
 * Preset, conversational tour experience (no AI needed)
 * Guides users through DIZEE with clickable options
 * Like Intercom tours but with DIZEE's style
 */

// Tour section configurations with colors
export const TOUR_SECTIONS = {
    bookings: {
        name: 'Bookings',
        color: '#FFD700', // Yellow - matches booking status colors
        route: '/bookings',
        icon: '🎤'
    },
    catalog: {
        name: 'Catalog',
        color: '#4A9EFF', // Blue - music/catalog theme
        route: '/catalog',
        icon: '🎵'
    },
    knowledge: {
        name: 'Knowledge',
        color: '#A78BFA', // Purple - knowledge/learning theme
        route: '/knowledge-base',
        icon: '📚'
    },
    flows: {
        name: 'Workflows',
        color: '#FF2300', // DIZEE Orange/Red - primary brand color
        route: '/flows',
        icon: '⚡'
    },
    tourmanager: {
        name: 'Tour Manager',
        color: '#34C759', // Green - travel/success theme
        route: '/tour-manager',
        icon: '🚌'
    },
    notes: {
        name: 'Notes',
        color: '#FF9500', // Orange - creative/notes theme
        route: '/notes',
        icon: '📝'
    },
    connectors: {
        name: 'Connectors',
        color: '#007AFF', // Blue - connection/tech theme
        route: '/connectors',
        icon: '🔗'
    },
    analytics: {
        name: 'Analytics',
        color: '#5856D6', // Purple - data/analytics theme
        route: '/analytics',
        icon: '📊'
    },
    contacts: {
        name: 'Contacts',
        color: '#32D74B', // Green - people/contacts theme
        route: '/contacts',
        icon: '👥'
    }
};

// Welcome message (first thing user sees)
export const getWelcomeMessage = (userName = 'there') => {
    return {
        role: 'assistant',
        text: `Hey ${userName}! Welcome to DIZEE.

I'm here to help you get started and show you around. Think of me as your personal tour guide  I'll walk you through everything you need to know.

**Ready to dive in?** I can teach you about:`,
        showOptions: true,
        options: Object.keys(TOUR_SECTIONS),
        showQuestions: false, // Don't show preset questions on main welcome
        isTourMessage: true
    };
};

// Section-specific welcome message (when arriving at a section)
export const getSectionWelcomeMessage = (sectionKey) => {
    const section = TOUR_SECTIONS[sectionKey];
    if (!section) return null;
    
    // Extract the description from the intro text (text after "Navigating to your [App] app\n\n")
    const introText = TOUR_CONTENT[sectionKey]?.intro?.text || '';
    const descriptionMatch = introText.match(/Navigating to your .+? app\n\n(.+)/s);
    const description = descriptionMatch ? descriptionMatch[1].trim() : '';
    
    const welcomeMessages = {
        bookings: `I'm here to help you with `,
        catalog: `I'm here to help you with `,
        knowledge: `I'm here to help you with `,
        flows: `I'm here to help you with `,
        tourmanager: `I'm here to help you with `,
        notes: `I'm here to help you with `,
        connectors: `I'm here to help you with `,
        analytics: `I'm here to help you with `,
        contacts: `I'm here to help you with `
    };
    
    const continuationText = `

${description}

I can answer questions about this section or help you explore other parts of DIZEE. Pick a question below or ask me anything:`;
    
    // Get section-specific questions instead of other app buttons
    const sectionQuestions = SECTION_QA[sectionKey]?.questions || [];
    
    // Add "Explore More" as the 6th option to show all apps
    const questionsWithExplore = [...sectionQuestions.slice(0, 5), 'Explore More'];
    
    return {
        role: 'assistant',
        text: welcomeMessages[sectionKey],
        continuationText: continuationText,
        showCurrentSection: sectionKey,
        showQuestions: true,
        showOptions: true,
        options: questionsWithExplore,
        isTourMessage: true,
        isInSection: true // Flag to indicate we're in a specific section
    };
};

// Tour content for each section
export const TOUR_CONTENT = {
    bookings: {
        intro: {
            navigationText: `Navigating to your Bookings app`,
            description: `This is where you manage all your shows, gigs, and events. It's basically your tour calendar on steroids.`,
            text: `Navigating to your Bookings app

This is where you manage all your shows, gigs, and events. It's basically your tour calendar on steroids.`,
            navigate: '/bookings',
            delay: 1500 // Wait 1.5s before navigating
        },
        onArrival: {
            text: `Welcome to your Bookings hub!

Here's what you can do:

• **Add shows** - Quick add or detailed entry
• **Track offers** - Keep tabs on pending gigs
• **Manage details** - Venue info, payment, notes
• **See your calendar** - Visual timeline of your tour

Want to try adding a practice show? Just say "add a show" or we can move on to the next section!`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** You can forward booking emails directly to DIZEE and I'll auto-create the shows for you. Pretty cool, right?`,
            `**Another tip:** Click any show to see or edit all the details. You can track payments, add notes, and more!`
        ]
    },
    
    catalog: {
        intro: {
            navigationText: `Navigating to your Catalog app`,
            description: `This is your music library where all your tracks, EPs, and albums live. Everything organized and easy to find.`,
            text: `Navigating to your Catalog app

This is your music library where all your tracks, EPs, and albums live. Everything organized and easy to find.`,
            navigate: '/catalog',
            delay: 1500
        },
        onArrival: {
            text: `Welcome to your music vault! 

Here's what you can manage:

• **Your discography** - All tracks in one place
• **Release dates** - Track what's out and what's coming
• **Metadata** - Genres, moods, collaborators
• **Audio files** - Store your actual tracks

Want to add a track to see how it works? Or should we keep exploring?`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Quick tip:** You can add full albums at once instead of track by track. Saves a ton of time! `,
            `**Fun fact:** Tag your tracks by mood or vibe to find them easier later. Great for licensing!`
        ]
    },
    
    knowledge: {
        intro: {
            navigationText: `Navigating to your Knowledge Base app`,
            description: `Think of this as your digital filing cabinet – perfect for storing contracts, contacts, press kits, basically anything important.`,
            text: `Navigating to your Knowledge Base app

Think of this as your digital filing cabinet – perfect for storing contracts, contacts, press kits, basically anything important.`,
            navigate: '/knowledge-base',
            delay: 1500
        },
        onArrival: {
            text: `Your personal knowledge vault! 

Store and organize:

• **Contracts** - Keep agreements safe and searchable
• **Contacts** - Venue managers, promoters, team
• **Documents** - Press photos, bios, riders
• **Important info** - Anything you need to remember

Everything's searchable, so you can find what you need instantly.

Ready for the last stop on the tour?`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** Use tags to organize your knowledge items. Makes finding stuff super easy!`,
            `**Note:** You can upload almost any file type  PDFs, images, docs, you name it.`
        ]
    },
    
    flows: {
        intro: {
            navigationText: `Navigating to your Workflows app`,
            description: `This is where DIZEE's automation magic happens. Set up workflows that trigger automatically based on your bookings and releases.`,
            text: `Navigating to your Workflows app

This is where DIZEE's automation magic happens. Set up workflows that trigger automatically based on your bookings and releases.`,
            navigate: '/flows',
            delay: 1500
        },
        onArrival: {
            text: `Welcome to your Workflows hub!

Here's what you can do:

• **Automated workflows** - Trigger actions when shows are confirmed or releases drop
• **Custom workflows** - Build your own automation sequences  
• **Smart notifications** - Get reminders at the perfect time
• **Team workflows** - Assign tasks automatically to band members or crew

Workflows save you hours by automating repetitive tasks!`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** Workflows can be triggered by events like "show confirmed" or "release date approaching"! •`,
            `**Smart feature:** Workflows can automatically assign tasks to team members and send notifications!`
        ]
    },

    tourmanager: {
        intro: {
            navigationText: `Navigating to your Tour Manager app`,
            description: `This is your comprehensive tour planning hub. Manage all aspects of your tours from routing to logistics.`,
            text: `Navigating to your Tour Manager app

This is your comprehensive tour planning hub. Manage all aspects of your tours from routing to logistics.`,
            navigate: '/tour-manager',
            delay: 1500
        },
        onArrival: {
            text: `Welcome to your Tour Manager hub!

Here's what you can do:

• **Plan tours** - Create and organize multi-city tours
• **Route optimization** - Find the best paths between venues
• **Logistics management** - Track travel, accommodation, and crew
• **Tour analytics** - See performance metrics across your tours

Tour Manager helps you coordinate complex tours with ease!`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** Use Tour Manager to optimize travel routes and save on transportation costs! •`,
            `**Smart feature:** Link Tour Manager with Bookings to automatically organize shows into tours!`
        ]
    },

    notes: {
        intro: {
            navigationText: `Navigating to your Notes app`,
            description: `Your personal workspace for ideas, lyrics, meeting notes, and creative thoughts.`,
            text: `Navigating to your Notes app

Your personal workspace for ideas, lyrics, meeting notes, and creative thoughts.`,
            navigate: '/notes',
            delay: 1500
        },
        onArrival: {
            text: `Welcome to your Notes hub!

Here's what you can do:

• **Creative notes** - Capture song ideas and lyrics
• **Meeting notes** - Record important discussions and decisions
• **Voice memos** - Quick audio notes on the go
• **Organize & search** - Tag and find your notes easily

Never lose a great idea again!`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** Use voice memos to capture melody ideas when you're away from instruments! •`,
            `**Smart feature:** Notes can be linked to specific projects or shows for better organization!`
        ]
    },

    connectors: {
        intro: {
            navigationText: `Navigating to your Connectors app`,
            description: `Connect DIZEE with your favorite tools like Google Calendar, Spotify, and social media platforms.`,
            text: `Navigating to your Connectors app

Connect DIZEE with your favorite tools like Google Calendar, Spotify, and social media platforms.`,
            navigate: '/connectors',
            delay: 1500
        },
        onArrival: {
            text: `Welcome to your Connectors hub!

Here's what you can do:

• **Calendar sync** - Connect Google Calendar, Apple Calendar
• **Music platforms** - Sync with Spotify, Apple Music, SoundCloud
• **Social media** - Connect Instagram, TikTok, Twitter
• **Automation** - Set up cross-platform workflows

Make DIZEE the center of your music ecosystem!`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** Calendar sync ensures your shows automatically appear in your personal calendar! •`,
            `**Smart feature:** Social media connectors can auto-post about upcoming shows!`
        ]
    },

    analytics: {
        intro: {
            navigationText: `Navigating to your Analytics app`,
            description: `Get insights into your music career with detailed analytics and performance metrics.`,
            text: `Navigating to your Analytics app

Get insights into your music career with detailed analytics and performance metrics.`,
            navigate: '/analytics',
            delay: 1500
        },
        onArrival: {
            text: `Welcome to your Analytics hub!

Here's what you can do:

• **Performance metrics** - Track streams, sales, and engagement
• **Tour analytics** - See which markets perform best
• **Growth tracking** - Monitor your career progression
• **Custom reports** - Generate insights for specific periods

Data-driven decisions for your music career!`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** Use analytics to identify your best-performing markets for tour planning! •`,
            `**Smart feature:** Analytics can predict optimal release timing based on your audience data!`
        ]
    },

    contacts: {
        intro: {
            navigationText: `Navigating to your Contacts app`,
            description: `Manage your professional network - venues, promoters, labels, and industry connections.`,
            text: `Navigating to your Contacts app

Manage your professional network - venues, promoters, labels, and industry connections.`,
            navigate: '/contacts',
            delay: 1500
        },
        onArrival: {
            text: `Welcome to your Contacts hub!

Here's what you can do:

• **Industry contacts** - Store venue owners, promoters, labels
• **Smart organization** - Tag contacts by type and location
• **Communication history** - Track all interactions
• **Quick actions** - Email, call, or message directly from DIZEE

Your professional network, organized and accessible!`,
            showOptions: true,
            options: ['continue', 'menu']
        },
        tips: [
            `**Pro tip:** Tag contacts by city to quickly find local connections when touring! •`,
            `**Smart feature:** Contacts can be linked to bookings to track venue relationships!`
        ]
    }
};

// Menu options message (shown when user wants to see options again)
export const getMenuMessage = () => {
    return {
        role: 'assistant',
        text: `No problem! Here's what I can show you:`,
        showOptions: true,
        options: Object.keys(TOUR_SECTIONS),
        isTourMessage: true
    };
};

// Preset questions users can ask about features
export const PRESET_QUESTIONS = [
    "How to add a show",
    "How to add an upcoming song",
    "How to set up Workflows",
    "How to utilize the knowledge base",
    "How to use the Sidebar"
];

// Section-specific Q&A for context-aware responses
export const SECTION_QA = {
    bookings: {
        questions: [
            "How to add a show",
            "How to bulk import shows",
            "How to track show offers",
            "How to manage show details",
            "How to view show calendar"
        ],
        answers: {
            "How to add a show": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a show at The Roxy on Nov 15" and I'll automatically create it for you.

**Drop In:**
Drop a screenshot of your tour dates directly into the AI chat bar, and I'll extract all the show details and add them to your Bookings.

**From the Bookings App**

**Bulk Import:**
Upload a CSV, Excel, or JSON file to import multiple shows at once.

**Manual Add:**
Open Bookings → Add → Manual Add (top right corner) and fill in all show details manually.

**Tip:** Confirmed shows trigger the workflows so make sure to set the status of the show (Pending, Confirmed etc) if it's not automatically set.`,
            "How to add an upcoming song": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a song called Cash Out dropping July 15" and I'll create the release in your Catalog.

**Drop In:**
Drop in a screenshot of release details directly into the AI chat bar, and I'll create the release in your Catalog.

**From the Catalog App**

**Manual Add:**
Open Catalog → Add → Manual Add (top right corner) and fill in all release details manually.

**Tip:**
Upcoming releases trigger workflows, so make sure to set the status of the release (Upcoming, Pending, etc.) if it's not auto set.`,
            "How to set up Workflows": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Set up a workflow to remind me 2 hours before every show" and I'll create it automatically.

**From the Workflows App**

**Manual Add:**
Open Workflows → Create Workflow (top right corner) and build your automation step by step.

**Tip:**
Workflows can trigger multiple actions and can be assigned to team members for automatic task distribution.`,
            "How to utilize the knowledge base": `**From the Home Screen**

**Quick Access:**
Type any question into the AI chat bar, "How do I pitch for Spotify editorial playlists?" and I'll pull the most relevant articles for you.

**From the Knowledge App**

**Browse Articles:**
Go to Knowledge → All Articles to explore topics by category such as Touring, Pitching, Registrations and more.

**Search:**
Use the Search bar at the top to quickly find guides or templates related to your question.

**Tip:**
Tap the icon on any article to save your favorites and access them later under the Bookmarked tab.`,
            "How to use the Sidebar": `**Your sidebar gives you quick access to your AI conversations, apps, artist accounts, and settings.**

**Conversation History**
Reopen, rename or delete any past chat by selecting it from your list. Start a new one anytime by clicking New Chat.

**Artist Account Switcher**
Tap your artist profile to switch between other accounts you have access to.

**Progress Bar**
Track how much of your monthly plan you've used. This updates automatically as you use Dizee.

**Settings & Logout**
Manage your preferences or log out using the options at the bottom of the sidebar.`
        }
    },
    catalog: {
        questions: [
            "How to add an upcoming song",
            "How to import from Spotify",
            "How to manage releases",
            "How to track song performance",
            "How to organize catalog"
        ],
        answers: {
            "How to add a show": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a show at The Roxy on Nov 15" and I'll automatically create it for you.

**Drop In:**
Drop a screenshot of your tour dates directly into the AI chat bar, and I'll extract all the show details and add them to your Bookings.

**From the Bookings App**

**Bulk Import:**
Upload a CSV, Excel, or JSON file to import multiple shows at once.

**Manual Add:**
Open Bookings → Add → Manual Add (top right corner) and fill in all show details manually.

**Tip:** Confirmed shows trigger the workflows so make sure to set the status of the show (Pending, Confirmed etc) if it's not automatically set.`,
            "How to add an upcoming song": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a song called Cash Out dropping July 15" and I'll create the release in your Catalog.

**Drop In:**
Drop in a screenshot of release details directly into the AI chat bar, and I'll create the release in your Catalog.

**From the Catalog App**

**Manual Add:**
Open Catalog → Add → Manual Add (top right corner) and fill in all release details manually.

**Tip:**
Upcoming releases trigger workflows, so make sure to set the status of the release (Upcoming, Pending, etc.) if it's not auto set.`,
            "How to set up Workflows": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Set up a workflow to remind me 2 hours before every show" and I'll create it automatically.

**From the Workflows App**

**Manual Add:**
Open Workflows → Create Workflow (top right corner) and build your automation step by step.

**Tip:**
Workflows can trigger multiple actions and can be assigned to team members for automatic task distribution.`,
            "How to utilize the knowledge base": `**From the Home Screen**

**Quick Access:**
Type any question into the AI chat bar, "How do I pitch for Spotify editorial playlists?" and I'll pull the most relevant articles for you.

**From the Knowledge App**

**Browse Articles:**
Go to Knowledge → All Articles to explore topics by category such as Touring, Pitching, Registrations and more.

**Search:**
Use the Search bar at the top to quickly find guides or templates related to your question.

**Tip:**
Tap the icon on any article to save your favorites and access them later under the Bookmarked tab.`,
            "How to use the Sidebar": `**Your sidebar gives you quick access to your AI conversations, apps, artist accounts, and settings.**

**Conversation History**
Reopen, rename or delete any past chat by selecting it from your list. Start a new one anytime by clicking New Chat.

**Artist Account Switcher**
Tap your artist profile to switch between other accounts you have access to.

**Progress Bar**
Track how much of your monthly plan you've used. This updates automatically as you use Dizee.

**Settings & Logout**
Manage your preferences or log out using the options at the bottom of the sidebar.`
        }
    },
    knowledge: {
        questions: [
            "How to utilize the knowledge base",
            "How to upload documents",
            "How to search knowledge",
            "How to organize articles",
            "How to share knowledge"
        ],
        answers: {
            "How to add a show": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a show at The Roxy on Nov 15" and I'll automatically create it for you.

**Drop In:**
Drop a screenshot of your tour dates directly into the AI chat bar, and I'll extract all the show details and add them to your Bookings.

**From the Bookings App**

**Bulk Import:**
Upload a CSV, Excel, or JSON file to import multiple shows at once.

**Manual Add:**
Open Bookings → Add → Manual Add (top right corner) and fill in all show details manually.

**Tip:** Confirmed shows trigger the workflows so make sure to set the status of the show (Pending, Confirmed etc) if it's not automatically set.`,
            "How to add an upcoming song": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a song called Cash Out dropping July 15" and I'll create the release in your Catalog.

**Drop In:**
Drop in a screenshot of release details directly into the AI chat bar, and I'll create the release in your Catalog.

**From the Catalog App**

**Manual Add:**
Open Catalog → Add → Manual Add (top right corner) and fill in all release details manually.

**Tip:**
Upcoming releases trigger workflows, so make sure to set the status of the release (Upcoming, Pending, etc.) if it's not auto set.`,
            "How to set up Workflows": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Set up a workflow to remind me 2 hours before every show" and I'll create it automatically.

**From the Workflows App**

**Manual Add:**
Open Workflows → Create Workflow (top right corner) and build your automation step by step.

**Tip:**
Workflows can trigger multiple actions and can be assigned to team members for automatic task distribution.`,
            "How to utilize the knowledge base": `**From the Home Screen**

**Quick Access:**
Type any question into the AI chat bar, "How do I pitch for Spotify editorial playlists?" and I'll pull the most relevant articles for you.

**From the Knowledge App**

**Browse Articles:**
Go to Knowledge → All Articles to explore topics by category such as Touring, Pitching, Registrations and more.

**Search:**
Use the Search bar at the top to quickly find guides or templates related to your question.

**Tip:**
Tap the icon on any article to save your favorites and access them later under the Bookmarked tab.`,
            "How to use the Sidebar": `**Your sidebar gives you quick access to your AI conversations, apps, artist accounts, and settings.**

**Conversation History**
Reopen, rename or delete any past chat by selecting it from your list. Start a new one anytime by clicking New Chat.

**Artist Account Switcher**
Tap your artist profile to switch between other accounts you have access to.

**Progress Bar**
Track how much of your monthly plan you've used. This updates automatically as you use Dizee.

**Settings & Logout**
Manage your preferences or log out using the options at the bottom of the sidebar.`
        }
    },
    flows: {
        questions: [
            "How to set up Workflows",
            "How to create automation",
            "How to trigger workflows",
            "How to assign team tasks",
            "How to monitor workflows"
        ],
        answers: {
            "How to add a show": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a show at The Roxy on Nov 15" and I'll automatically create it for you.

**Drop In:**
Drop a screenshot of your tour dates directly into the AI chat bar, and I'll extract all the show details and add them to your Bookings.

**From the Bookings App**

**Bulk Import:**
Upload a CSV, Excel, or JSON file to import multiple shows at once.

**Manual Add:**
Open Bookings → Add → Manual Add (top right corner) and fill in all show details manually.

**Tip:** Confirmed shows trigger the workflows so make sure to set the status of the show (Pending, Confirmed etc) if it's not automatically set.`,
            "How to add an upcoming song": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a song called Cash Out dropping July 15" and I'll create the release in your Catalog.

**Drop In:**
Drop in a screenshot of release details directly into the AI chat bar, and I'll create the release in your Catalog.

**From the Catalog App**

**Manual Add:**
Open Catalog → Add → Manual Add (top right corner) and fill in all release details manually.

**Tip:**
Upcoming releases trigger workflows, so make sure to set the status of the release (Upcoming, Pending, etc.) if it's not auto set.`,
            "How to set up Workflows": `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Set up a workflow to remind me 2 hours before every show" and I'll create it automatically.

**From the Workflows App**

**Manual Add:**
Open Workflows → Create Workflow (top right corner) and build your automation step by step.

**Tip:**
Workflows can trigger multiple actions and can be assigned to team members for automatic task distribution.`,
            "How to utilize the knowledge base": `**From the Home Screen**

**Quick Access:**
Type any question into the AI chat bar, "How do I pitch for Spotify editorial playlists?" and I'll pull the most relevant articles for you.

**From the Knowledge App**

**Browse Articles:**
Go to Knowledge → All Articles to explore topics by category such as Touring, Pitching, Registrations and more.

**Search:**
Use the Search bar at the top to quickly find guides or templates related to your question.

**Tip:**
Tap the icon on any article to save your favorites and access them later under the Bookmarked tab.`,
            "How to use the Sidebar": `**Your sidebar gives you quick access to your AI conversations, apps, artist accounts, and settings.**

**Conversation History**
Reopen, rename or delete any past chat by selecting it from your list. Start a new one anytime by clicking New Chat.

**Artist Account Switcher**
Tap your artist profile to switch between other accounts you have access to.

**Progress Bar**
Track how much of your monthly plan you've used. This updates automatically as you use Dizee.

**Settings & Logout**
Manage your preferences or log out using the options at the bottom of the sidebar.`
        }
    },

    tourmanager: {
        questions: [
            "How to plan a tour",
            "How to optimize routes",
            "How to manage logistics",
            "How to track tour analytics",
            "How to coordinate crew"
        ],
        answers: {
            "How to plan a tour": `**From Tour Manager**

**Create Tour:**
Go to Tour Manager → New Tour and set up your tour details.

**Add Shows:**
Link existing bookings or create new shows directly in the tour.

**Route Planning:**
Use the route optimizer to find the most efficient path between venues.

**Tip:** Tour Manager automatically calculates travel times and suggests optimal routing.`
        }
    },

    notes: {
        questions: [
            "How to create notes",
            "How to record voice memos",
            "How to organize notes",
            "How to search notes",
            "How to share notes"
        ],
        answers: {
            "How to create notes": `**From Notes App**

**Quick Note:**
Click the + button and start typing your thoughts.

**Voice Memo:**
Use the microphone button to record audio notes on the go.

**Organization:**
Tag your notes with categories like #lyrics, #ideas, #meetings.

**Tip:** Notes sync across all your devices so you never lose an idea.`
        }
    },

    connectors: {
        questions: [
            "How to connect calendar",
            "How to sync Spotify",
            "How to link social media",
            "How to set up automation",
            "How to manage connections"
        ],
        answers: {
            "How to connect calendar": `**From Connectors App**

**Google Calendar:**
Go to Connectors → Calendar → Connect Google Calendar.

**Apple Calendar:**
Select Apple Calendar and follow the authorization steps.

**Sync Settings:**
Choose what events to sync (shows, meetings, deadlines).

**Tip:** Calendar sync ensures your DIZEE shows appear in your personal calendar automatically.`
        }
    },

    analytics: {
        questions: [
            "How to view performance",
            "How to track growth",
            "How to generate reports",
            "How to analyze markets",
            "How to export data"
        ],
        answers: {
            "How to view performance": `**From Analytics App**

**Dashboard:**
See your key metrics at a glance on the main dashboard.

**Detailed Reports:**
Click on any metric to dive deeper into the data.

**Time Periods:**
Select different date ranges to compare performance.

**Tip:** Use analytics to identify your best-performing markets for future tour planning.`
        }
    },

    contacts: {
        questions: [
            "How to add contacts",
            "How to organize contacts",
            "How to track interactions",
            "How to search contacts",
            "How to export contacts"
        ],
        answers: {
            "How to add contacts": `**From Contacts App**

**Manual Add:**
Click + and fill in contact details manually.

**Import:**
Upload a CSV file or sync from your phone contacts.

**Quick Add:**
Add contacts directly from emails or booking communications.

**Tip:** Tag contacts by type (venue, promoter, label) and location for easy filtering.`
        }
    }
};

// Answers to preset questions
export const QUESTION_ANSWERS = {
    "How to add a show": {
        text: `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a show at The Roxy on Nov 15" and I'll automatically create it for you.

**Drop In:**
Drop a screenshot of your tour dates directly into the AI chat bar, and I'll extract all the show details and add them to your Bookings.

**From the Bookings App**

**Bulk Import:**
Upload a CSV, Excel, or JSON file to import multiple shows at once.

**Manual Add:**
Open Bookings → Add → Manual Add (top right corner) and fill in all show details manually.

**Tip:** Confirmed shows trigger the workflows so make sure to set the status of the show (Pending, Confirmed etc) if it's not automatically set.

Want to see the Bookings section?`,
        showOptions: true,
        options: ['bookings', 'menu']
    },
    "How to add an upcoming song": {
        text: `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Add a song called Cash Out dropping July 15" and I'll create the release in your Catalog.

**Drop In:**
Drop in a screenshot of release details directly into the AI chat bar, and I'll create the release in your Catalog.

**From the Catalog App**

**Manual Add:**
Open Catalog → Add → Manual Add (top right corner) and fill in all release details manually.

**Tip:**
Upcoming releases trigger workflows, so make sure to set the status of the release (Upcoming, Pending, etc.) if it's not auto set.

Want to check out the Catalog?`,
        showOptions: true,
        options: ['catalog', 'menu']
    },
    "How to set up Workflows": {
        text: `**From the Home Screen**

**Quick Add:**
Just type in the AI chat bar: "Set up a workflow to remind me 2 hours before every show" and I'll create it automatically.

**From the Workflows App**

**Manual Add:**
Open Workflows → Create Workflow (top right corner) and build your automation step by step.

**Tip:**
Workflows can trigger multiple actions and can be assigned to team members for automatic task distribution.

Want to explore Workflows?`,
        showOptions: true,
        options: ['reminders', 'menu']
    },
    "How to utilize the knowledge base": {
        text: `**From the Home Screen**

**Quick Access:**
Type any question into the AI chat bar, "How do I pitch for Spotify editorial playlists?" and I'll pull the most relevant articles for you.

**From the Knowledge App**

**Browse Articles:**
Go to Knowledge → All Articles to explore topics by category such as Touring, Pitching, Registrations and more.

**Search:**
Use the Search bar at the top to quickly find guides or templates related to your question.

**Tip:**
Tap the icon on any article to save your favorites and access them later under the Bookmarked tab.

Want to see it?`,
        showOptions: true,
        options: ['knowledge', 'menu']
    },
    "How to use the Sidebar": {
        text: `**Your sidebar gives you quick access to your AI conversations, apps, artist accounts, and settings.**

**Conversation History**
Reopen, rename or delete any past chat by selecting it from your list. Start a new one anytime by clicking New Chat.

**Artist Account Switcher**
Tap your artist profile to switch between other accounts you have access to.

**Progress Bar**
Track how much of your monthly plan you've used. This updates automatically as you use Dizee.

**Settings & Logout**
Manage your preferences or log out using the options at the bottom of the sidebar.`,
        showOptions: true,
        options: ['menu']
    }
};

// Completion message
export const getCompletionMessage = () => {
    return {
        role: 'assistant',
        text: `Awesome! You're all set! •

You now know your way around DIZEE. Remember, I'm always here to help:

• Just ask me anything in regular chat
• Click the prompts below if you have questions
• Start using DIZEE  you got this! •

Now go make some magic happen! `,
        showQuestions: true,
        isTourMessage: true
    };
};

// Quick responses for common actions
export const QUICK_RESPONSES = {
    continue: `Perfect! Let's move on to the next section...`,
    menu: `Sure! Let me show you the options again...`,
    done: `Got it! You're ready to rock! •`,
    addShow: `Great! I can help you add a show. Just tell me the venue and date, like "Add a show at The Roxy on Nov 15"`,
    addTrack: `Awesome! I can help you add a track. Just tell me the title, like "Add my track 'Summer Vibes'"`
};

// Determine next section in tour
export const getNextSection = (currentSection) => {
    const sections = Object.keys(TOUR_SECTIONS);
    const currentIndex = sections.indexOf(currentSection);
    
    if (currentIndex < sections.length - 1) {
        return sections[currentIndex + 1];
    }
    
    return null; // Tour complete
};

// Get section info by name
export const getSectionInfo = (sectionName) => {
    return TOUR_SECTIONS[sectionName] || null;
};

// Check if tour is active
export const isTourActive = () => {
    return sessionStorage.getItem('tourActive') === 'true';
};

// Set tour active state
export const setTourActive = (active) => {
    if (active) {
        sessionStorage.setItem('tourActive', 'true');
        sessionStorage.setItem('tourStartTime', Date.now().toString());
    } else {
        sessionStorage.removeItem('tourActive');
        sessionStorage.removeItem('tourCurrentSection');
        sessionStorage.removeItem('tourStartTime');
    }
};

// Get current tour section
export const getCurrentTourSection = () => {
    return sessionStorage.getItem('tourCurrentSection');
};

// Set current tour section
export const setCurrentTourSection = (section) => {
    if (section) {
        sessionStorage.setItem('tourCurrentSection', section);
    } else {
        sessionStorage.removeItem('tourCurrentSection');
    }
};

// Mark tour as completed
export const completeTour = () => {
    setTourActive(false);
    localStorage.setItem('tourCompleted', Date.now().toString());
    localStorage.setItem('accountSetupCompleted', Date.now().toString());
};

// Check if user has completed tour
export const hasCompletedTour = () => {
    return localStorage.getItem('tourCompleted') !== null;
};

// Save tour messages to sessionStorage for syncing between pages
export const saveTourMessages = (messages) => {
    try {
        sessionStorage.setItem('tourMessages', JSON.stringify(messages));
    } catch (error) {
        console.error('Error saving tour messages:', error);
    }
};

// Load tour messages from sessionStorage
export const loadTourMessages = () => {
    try {
        const saved = sessionStorage.getItem('tourMessages');
        return saved ? JSON.parse(saved) : [];
    } catch (error) {
        console.error('Error loading tour messages:', error);
        return [];
    }
};

// Clear tour messages from sessionStorage
export const clearTourMessages = () => {
    try {
        sessionStorage.removeItem('tourMessages');
    } catch (error) {
        console.error('Error clearing tour messages:', error);
    }
};

